/**
 * Fabrique des blocs Puck de la categorie "content" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { GalleryPickerField } from "@/components/cms-puck/fields/GalleryPickerField";
import { MediaPickerField } from "@/components/cms-puck/fields/MediaPickerField";
import { MediaSizeField } from "@/components/cms-puck/fields/MediaSizeField";
import { NewsIdsField } from "@/components/cms-puck/fields/NewsPickerField";
import { PuckBlockRichTextEditor } from "@/components/cms-puck/fields/PuckRichTextEditor";
import { CMS_ICON_NAMES } from "@/lib/cms-puck/blocks/icons";
import { PdfReaderBlockPreview } from "@/components/cms-puck/previews/PdfReaderBlockPreview";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { DEFAULT_BUTTON_APPEARANCE, DEFAULT_BUTTON_LINK, DEFAULT_OPTIONAL_LINK, type ButtonAppearance, type ButtonLink } from "@/lib/cms-puck/blocks/button";
import { ButtonAppearanceField } from "@/components/cms-puck/fields/ButtonAppearanceField";
import { IconPickerField } from "@/components/cms-puck/fields/IconPickerField";
import { LinkTargetField } from "@/components/cms-puck/fields/LinkTargetField";
import {
  galleryClickableField,
  imageClickableField,
  optionalLinkField,
} from "@/components/cms-puck/fields/media-interaction-fields";
import { PdfPickerField } from "@/components/cms-puck/fields/PdfPickerField";
import { SocialLinksField } from "@/components/cms-puck/fields/SocialLinksField";
import { normalizeLogoGridColumns, normalizeLogoGridSize } from "@/lib/cms-puck/blocks/logo-grid";
import { normalizeMapAspect } from "@/lib/cms-puck/blocks/map-embed";
import { type SocialLinkItem } from "@/lib/cms-puck/blocks/social-links";
import { normalizeVideoHeroHeight, normalizeVideoHeroOverlay } from "@/lib/cms-puck/blocks/video-hero";
import { puckMediaSizeFieldOptions } from "@/lib/cms-puck/media-utils";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { NewsPreview } from "@/components/cms-puck/blocks/NewsPreview";
import { CarouselBlockPreview, GalleryBlockPreview } from "@/components/cms-puck/blocks/gallery-blocks";
import { ButtonBlock, PreviewButton } from "@/components/cms-puck/blocks/button-blocks";
import { IconBlock, IconColorField } from "@/components/cms-puck/blocks/icon-blocks";
import { HeadingBlock, QuoteBlock, RichTextBlock } from "@/components/cms-puck/blocks/text-blocks";
import { LogoGridBlockPreview, MediaBlock, TestimonialsBlockPreview, VideoHeroBlockPreview } from "@/components/cms-puck/blocks/media-blocks";
import { MapEmbedBlockPreview, SocialLinksBlockPreview } from "@/components/cms-puck/blocks/misc-blocks";
import { TEXT_ALIGN_CLASS } from "@/components/cms-puck/blocks/shared";
import { type CustomField } from "@measured/puck";
import { compositeInnerGapAlignFields } from "@/lib/cms-puck/layout";
import { type RawComponentsConfig } from "../types";

export function buildContentBlocks({
  galleryColumnCountField,
  newsColumnCountField,
}: {
  galleryColumnCountField: CustomField<number>;
  newsColumnCountField: CustomField<2 | 3>;
}) {
  return {
    /* ------------------------------------------------------------------ */
    /* Blocs de contenu simples (Lot 4)                                   */
    /* Droppables DANS les slots des conteneurs (Container/Columns/Grid).  */
    /* Champs traduisibles déclarés dans TRANSLATABLE_FIELDS (api.ts) ;     */
    /* extraction i18n récursive automatique quand imbriqués. Aperçus       */
    /* fonctionnels, parité visuelle avec le front public.                  */
    /* ------------------------------------------------------------------ */

    Heading: {
      label: cmsNew("cms.new.blocks.heading.label", "Titre"),
      fields: {
        level: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.niveau_structurel",
            "Niveau (structurel)",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.h1", "H1"), value: "h1" },
            { label: cmsNew("cms.new.auto.label.h2", "H2"), value: "h2" },
            { label: cmsNew("cms.new.auto.label.h3", "H3"), value: "h3" },
            { label: cmsNew("cms.new.auto.label.h4", "H4"), value: "h4" },
            { label: cmsNew("cms.new.auto.label.h5", "H5"), value: "h5" },
            { label: cmsNew("cms.new.auto.label.h6", "H6"), value: "h6" },
          ],
        },
        // `contentEditable` : éditable directement sur le canvas (in-place), en
        // plus de l'input dans la sidebar. Écrit dans `props.text` via le même
        // dispatch que la sidebar → i18n intact (diffStrings → ContentString).
        text: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.texte_traduisible",
            "Texte (traduisible)",
          ),
          contentEditable: true,
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        link: optionalLinkField(cmsNew),
      },
      defaultProps: {
        level: "h2",
        text: "Titre",
        align: "left",
        link: { ...DEFAULT_OPTIONAL_LINK },
      },
      // `style` injecté par withStyle (absent du type raw) → lu via cast. Le
      // traitement de texte est appliqué inline sur le `<Hn>` (cf. HeadingBlock).
      // Le lien (optionnel) est rendu côté front (PuckHeading) ; l'aperçu BO
      // affiche le titre sans l'ancre.
      render: ({ level, text, align, ...rest }) => (
        <HeadingBlock
          level={level}
          text={text}
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    RichText: {
      label: cmsNew("cms.new.auto.label.texte", "Texte"),
      fields: {
        // `body` = HTML TRADUISIBLE (clé `<blockId>.body`, assaini au save API).
        // Édité ICI dans la sidebar (le canvas Puck bloque la sélection de texte
        // inline via `user-select: none` sur ses blocs draggables).
        body: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.contenu_traduisible",
            "Contenu (traduisible)",
          ),
          render: ({ value, onChange }) => (
            <PuckBlockRichTextEditor
              value={value as string | undefined}
              onChange={onChange}
            />
          ),
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        link: optionalLinkField(cmsNew),
      },
      defaultProps: {
        body: "<p>Votre texte ici…</p>",
        align: "left",
        link: { ...DEFAULT_OPTIONAL_LINK },
      },
      // Lien optionnel rendu côté front (PuckLinkWrap) ; l'aperçu BO affiche
      // le texte sans l'ancre (parité Heading).
      render: ({ body, align, ...rest }) => (
        <RichTextBlock
          body={body}
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    Button: {
      label: cmsNew("cms.new.blocks.button.label", "Bouton"),
      fields: {
        label: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_traduisible",
            "Libellé (traduisible)",
          ),
          contentEditable: true,
        },
        // Lien : page interne OU lien externe/personnalisé + nouvel onglet.
        link: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.lien", "Lien"),
          render: ({ value, onChange }) => (
            <LinkTargetField
              value={value as ButtonLink | undefined}
              onChange={onChange}
            />
          ),
        },
        variant: {
          type: "select",
          label: cmsNew("cms.new.auto.label.style_de_base", "Style de base"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.principal", "Principal"),
              value: "primary",
            },
            {
              label: cmsNew("cms.new.auto.label.secondaire", "Secondaire"),
              value: "secondary",
            },
            {
              label: cmsNew("cms.new.auto.label.contour", "Contour"),
              value: "outline",
            },
            {
              label: cmsNew("cms.new.auto.label.lien_texte", "Lien (texte seul)"),
              value: "link",
            },
          ],
        },
        iconName: {
          type: "select",
          label: cmsNew("cms.new.blocks.icon.label", "Icône"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.aucune", "Aucune"),
              value: "",
            },
            ...CMS_ICON_NAMES.map((n) => ({ label: n, value: n })),
          ],
        },
        iconPosition: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.position_de_licone",
            "Position de l'icône",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.avant", "Avant"),
              value: "before",
            },
            {
              label: cmsNew("cms.new.auto.label.apres", "Après"),
              value: "after",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        // Couleurs au survol. Bordures/arrondis : onglet Design PARTAGÉ depuis
        // l'unification (les anciennes valeurs `appearance` restent LUES ; le
        // style partagé prime quand il est défini).
        appearance: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.au_survol", "Au survol"),
          render: ({ value, onChange }) => (
            <ButtonAppearanceField
              value={value as ButtonAppearance | undefined}
              onChange={onChange}
              sections={["hover"]}
            />
          ),
        },
      },
      defaultProps: {
        label: cmsNew("cms.new.auto.label.en_savoir_plus", "En savoir plus"),
        link: { ...DEFAULT_BUTTON_LINK },
        variant: "primary",
        align: "left",
        iconName: "",
        iconPosition: "before",
        appearance: DEFAULT_BUTTON_APPEARANCE,
      },
      // Aperçu éditeur : rendu non cliquable (span stylé comme un bouton) — le
      // vrai lien (a / next/link) est rendu côté public. Style `"self"` : le
      // fond/texte/typo va sur le `<span>` (cf. ButtonBlock). `style` + `id` sont
      // injectés par Puck/withStyle (absents du type raw) → lus via cast.
      render: ({
        label,
        variant,
        align,
        iconName,
        iconPosition,
        ...rest
      }) => {
        const r = rest as {
          id?: string;
          appearance?: ButtonAppearance;
          style?: BlockStyle;
        };
        return (
          <ButtonBlock
            id={r.id}
            label={label}
            variant={variant}
            align={align}
            iconName={iconName || null}
            iconPosition={iconPosition}
            appearance={r.appearance}
            style={r.style}
          />
        );
      },
    },

    Download: {
      label: cmsNew(
        "cms.new.auto.label.telechargement_document_par_langue",
        "Téléchargement",
      ),
      fields: {
        label: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_du_bouton_traduisible",
            "Libellé du bouton (traduisible)",
          ),
          contentEditable: true,
        },
        // URL TRADUISIBLE : un document différent par langue (fallback langue
        // défaut si non renseignée) — parité documentUrl de l'Expertise.
        url: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.url_du_document_traduisible_par_langue",
            "URL du document (traduisible — par langue)",
          ),
        },
        variant: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.style_du_bouton_structurel",
            "Style du bouton (structurel)",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.principal", "Principal"),
              value: "primary",
            },
            {
              label: cmsNew("cms.new.auto.label.secondaire", "Secondaire"),
              value: "secondary",
            },
            {
              label: cmsNew("cms.new.auto.label.contour", "Contour"),
              value: "outline",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
      },
      defaultProps: {
        label: cmsNew(
          "cms.new.auto.label.telecharger_le_document",
          "Télécharger le document",
        ),
        url: "",
        variant: "primary",
        align: "left",
      },
      render: ({ label, variant, align, url }) => (
        <div className={TEXT_ALIGN_CLASS[align]}>
          <PreviewButton
            label={
              label ||
              cmsNew("cms.new.preview.download.fallbackLabel", "Télécharger")
            }
            variant={variant}
          />
          {!url ? (
            <p className="mt-1 text-[11px] text-amber-600">
              {cmsNew(
                "cms.new.preview.download.urlMissing",
                "URL du document non renseignée pour cette langue (fallback langue par défaut à la publication).",
              )}
            </p>
          ) : null}
        </div>
      ),
    },

    Quote: {
      label: cmsNew("cms.new.blocks.quote.label", "Citation"),
      fields: {
        text: {
          type: "textarea",
          label: cmsNew(
            "cms.new.auto.label.citation_traduisible",
            "Citation (traduisible)",
          ),
          contentEditable: true,
        },
        author: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.auteur_traduisible",
            "Auteur (traduisible)",
          ),
          contentEditable: true,
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        ...compositeInnerGapAlignFields({ withAlignItems: false }),
      },
      defaultProps: {
        text: "Une citation inspirante.",
        author: "",
        align: "left",
        gap: "sm",
      },
      render: ({ text, author, align, gap, gapCustomPx, ...rest }) => (
        <QuoteBlock
          text={text}
          author={author}
          align={align}
          gap={gap}
          gapCustomPx={gapCustomPx}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    Icon: {
      label: cmsNew("cms.new.blocks.icon.label", "Icône"),
      fields: {
        name: {
          type: "custom",
          label: cmsNew("cms.new.blocks.icon.label", "Icône"),
          render: ({ value, onChange }) => (
            <IconPickerField value={value as string} onChange={onChange} />
          ),
        },
        size: {
          type: "number",
          label: cmsNew("cms.new.auto.label.taille_px", "Taille (px)"),
          min: 8,
          max: 200,
        },
        color: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.couleur", "Couleur"),
          render: ({ value, onChange }) => (
            <IconColorField value={value} onChange={onChange} />
          ),
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        // Lien optionnel (réseaux sociaux, raccourcis…) — rendu côté front.
        link: optionalLinkField(cmsNew),
      },
      defaultProps: {
        name: "star",
        size: 32,
        color: null,
        align: "left",
        link: { ...DEFAULT_OPTIONAL_LINK },
      },
      // Style `"self"` : le fond/espacement va sur un span ajusté à l'icône.
      // `style` injecté par withStyle (absent du type raw) → lu via cast.
      render: ({ name, size, color, align, ...rest }) => (
        <IconBlock
          name={name}
          size={size}
          color={color}
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    Divider: {
      label: cmsNew("cms.new.blocks.divider.label", "Séparateur"),
      fields: {
        // Renommé `style` → `lineStyle` (collision avec le BlockStyle universel).
        lineStyle: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.style_de_ligne",
            "Style de ligne",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.plein", "Plein"),
              value: "solid",
            },
            {
              label: cmsNew("cms.new.auto.label.tirets", "Tirets"),
              value: "dashed",
            },
            {
              label: cmsNew("cms.new.auto.label.pointilles", "Pointillés"),
              value: "dotted",
            },
          ],
        },
        thickness: {
          type: "number",
          label: cmsNew("cms.new.auto.label.epaisseur_px", "Épaisseur (px)"),
          min: 1,
          max: 12,
        },
        spacing: {
          type: "number",
          label: cmsNew(
            "cms.new.auto.label.espacement_vertical_px",
            "Espacement vertical (px)",
          ),
          min: 0,
          max: 96,
        },
      },
      defaultProps: { lineStyle: "solid", thickness: 1, spacing: 0 },
      // Rétro-compat : un Divider déjà publié porte `props.style` (string). On le
      // lit en repli si `lineStyle` est absent — aucune migration nécessaire.
      render: ({ lineStyle, thickness, spacing, ...legacy }) => {
        const resolvedLine =
          lineStyle ??
          (typeof (legacy as { style?: unknown }).style === "string"
            ? ((legacy as { style?: string }).style as
                | "solid"
                | "dashed"
                | "dotted")
            : "solid");
        return (
          <hr
            style={{
              borderTopStyle: resolvedLine,
              borderTopWidth: `${thickness}px`,
              borderTopColor: "currentColor",
              opacity: 0.3,
              marginTop: `${spacing}px`,
              marginBottom: `${spacing}px`,
            }}
          />
        );
      },
    },

    Media: {
      label: cmsNew("cms.new.blocks.media.label", "Média"),
      fields: {
        // Id d'Attachment STRUCTUREL (partagé toutes langues — pas traduisible).
        // Le picker écrit l'id ; pour les IMAGES il pilote AUSSI la prop `size`
        // (mini-sélecteur de taille au clic + boutons de taille sous l'aperçu),
        // via un champ custom monté sur la prop OBJET `media` ? Non : l'id et la
        // taille restent DEUX props distinctes (id = ref ATTACHMENT, size = pure
        // affichage). La taille est aussi éditable via le champ `size` ci-dessous.
        attachmentId: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.media_image_video_embed",
            "Média (image / vidéo / embed)",
          ),
          render: ({ value, onChange }) => (
            <MediaPickerField
              value={value}
              onChange={onChange}
              fieldName="attachmentId"
              label=""
            />
          ),
        },
        // Taille d'affichage (image) — STRUCTURELLE (non traduisible). Mapping vers
        // les 4 URLs côté front (MEDIA-3). Vidéo/embed : ignorée (poster/player).
        // Custom : dims (WWxHHpx) calculées fit:inside selon le ratio de l'image.
        size: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.taille_image", "Taille (image)"),
          render: ({ value, onChange }) => (
            <MediaSizeField
              value={typeof value === "string" ? value : undefined}
              onChange={onChange}
            />
          ),
        },
        aspect: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.proportions_image",
            "Proportions (image)",
          ),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.auto_ratio_naturel",
                "Auto (ratio naturel)",
              ),
              value: "auto",
            },
            {
              label: cmsNew("cms.new.auto.label.carre_1_1", "Carré 1:1"),
              value: "1/1",
            },
            {
              label: cmsNew("cms.new.auto.label.paysage_4_3", "Paysage 4:3"),
              value: "4/3",
            },
            {
              label: cmsNew("cms.new.auto.label.photo_3_2", "Photo 3:2"),
              value: "3/2",
            },
            {
              label: cmsNew("cms.new.auto.label.large_16_9", "Large 16:9"),
              value: "16/9",
            },
          ],
        },
        fullWidth: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.largeur_image", "Largeur"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.largeur_taille_origine",
                "Taille d'origine",
              ),
              value: false,
            },
            {
              label: cmsNew(
                "cms.new.auto.label.largeur_pleine_bloc",
                "Pleine largeur du bloc",
              ),
              value: true,
            },
          ],
        },
        clickable: imageClickableField(cmsNew),
        link: optionalLinkField(cmsNew),
        caption: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.legende_traduisible",
            "Légende (traduisible)",
          ),
        },
      },
      // `size` défaut "large" : un bloc Media publié AVANT cette feature (sans
      // size) retombe sur "large" au rendu = comportement actuel (largePublicUrl).
      // `aspect` défaut "auto" = ratio naturel (rétro-compat).
      // `fullWidth` défaut false / `clickable` défaut true = comportement historique.
      // `link` optionnel (vide par défaut) → rendu côté front (PuckMedia).
      defaultProps: {
        attachmentId: "",
        caption: "",
        size: "large",
        aspect: "auto",
        fullWidth: false,
        clickable: true,
        link: { ...DEFAULT_OPTIONAL_LINK },
      },
      render: ({ attachmentId, caption, size, aspect, fullWidth, ...rest }) => (
        <MediaBlock
          attachmentId={attachmentId}
          caption={caption}
          size={size}
          aspect={aspect}
          fullWidth={fullWidth}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    Gallery: {
      label: cmsNew("cms.new.blocks.gallery.label", "Galerie"),
      fields: {
        attachmentIds: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.medias", "Médias"),
          render: ({ value, onChange }) => (
            <GalleryPickerField
              value={value as string[] | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: galleryColumnCountField,
        aspect: {
          type: "select",
          label: cmsNew("cms.new.auto.label.proportions", "Proportions"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.auto_ratio_naturel",
                "Auto (ratio naturel)",
              ),
              value: "auto",
            },
            {
              label: cmsNew("cms.new.auto.label.carre_1_1", "Carré 1:1"),
              value: "1/1",
            },
            {
              label: cmsNew("cms.new.auto.label.paysage_4_3", "Paysage 4:3"),
              value: "4/3",
            },
            {
              label: cmsNew("cms.new.auto.label.photo_3_2", "Photo 3:2"),
              value: "3/2",
            },
            {
              label: cmsNew("cms.new.auto.label.large_16_9", "Large 16:9"),
              value: "16/9",
            },
          ],
        },
        size: {
          type: "select",
          label: cmsNew("cms.new.auto.label.taille_image", "Taille (image)"),
          options: puckMediaSizeFieldOptions(cmsNew),
        },
        clickable: galleryClickableField(cmsNew),
      },
      defaultProps: {
        attachmentIds: [],
        columns: 3,
        aspect: "1/1",
        size: "large",
        clickable: true,
      },
      render: ({ attachmentIds, columns, aspect, ...rest }) => (
        <GalleryBlockPreview
          attachmentIds={attachmentIds}
          columns={columns}
          aspect={aspect}
          sampleUrls={(rest as { sampleUrls?: string[] }).sampleUrls}
        />
      ),
    },

    Carousel: {
      label: cmsNew("cms.new.auto.label.carousel", "Carousel"),
      fields: {
        attachmentIds: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.medias", "Médias"),
          render: ({ value, onChange }) => (
            <GalleryPickerField
              value={value as string[] | undefined}
              onChange={onChange}
            />
          ),
        },
        aspect: {
          type: "select",
          label: cmsNew("cms.new.auto.label.proportions", "Proportions"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.auto_ratio_naturel",
                "Auto (ratio naturel)",
              ),
              value: "auto",
            },
            {
              label: cmsNew("cms.new.auto.label.carre_1_1", "Carré 1:1"),
              value: "1/1",
            },
            {
              label: cmsNew("cms.new.auto.label.paysage_4_3", "Paysage 4:3"),
              value: "4/3",
            },
            {
              label: cmsNew("cms.new.auto.label.photo_3_2", "Photo 3:2"),
              value: "3/2",
            },
            {
              label: cmsNew("cms.new.auto.label.large_16_9", "Large 16:9"),
              value: "16/9",
            },
          ],
        },
        size: {
          type: "select",
          label: cmsNew("cms.new.auto.label.taille_image", "Taille (image)"),
          options: puckMediaSizeFieldOptions(cmsNew),
        },
        clickable: galleryClickableField(cmsNew),
      },
      defaultProps: {
        attachmentIds: [],
        aspect: "16/9",
        size: "large",
        clickable: true,
      },
      render: ({ attachmentIds, aspect }) => (
        <CarouselBlockPreview attachmentIds={attachmentIds} aspect={aspect} />
      ),
    },

    SocialLinks: {
      label: cmsNew("cms.new.auto.label.liens_sociaux", "Liens sociaux"),
      fields: {
        links: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.liens", "Liens"),
          render: ({ value, onChange }) => (
            <SocialLinksField
              value={value as SocialLinkItem[] | undefined}
              onChange={onChange}
            />
          ),
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
      },
      defaultProps: {
        links: [],
        align: "center",
      },
      render: ({ links, align, ...rest }) => (
        <SocialLinksBlockPreview
          links={links}
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    LogoGrid: {
      label: cmsNew(
        "cms.new.auto.label.logos_partenaires",
        "Logos partenaires",
      ),
      fields: {
        attachmentIds: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.logos", "Logos"),
          render: ({ value, onChange }) => (
            <GalleryPickerField
              value={value as string[] | undefined}
              onChange={onChange}
              allowedCatalogKinds={["image"]}
            />
          ),
        },
        columns: galleryColumnCountField,
        size: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.taille_des_logos",
            "Taille des logos",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.petit", "Petit"),
              value: "sm",
            },
            {
              label: cmsNew("cms.new.auto.label.moyen", "Moyen"),
              value: "md",
            },
            {
              label: cmsNew("cms.new.auto.label.grand", "Grand"),
              value: "lg",
            },
          ],
        },
        grayscale: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.niveaux_de_gris",
            "Niveaux de gris",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
      },
      defaultProps: {
        attachmentIds: [],
        columns: 4,
        size: "md",
        grayscale: true,
      },
      render: ({ attachmentIds, columns, size, grayscale }) => (
        <LogoGridBlockPreview
          attachmentIds={attachmentIds}
          columns={normalizeLogoGridColumns(columns)}
          size={normalizeLogoGridSize(size)}
          grayscale={grayscale}
        />
      ),
    },

    Testimonials: {
      label: cmsNew("cms.new.blocks.testimonials.label", "Témoignages"),
      fields: {
        layout: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.disposition", "Disposition"),
          options: [
            {
              label: cmsNew("cms.new.blocks.grid.label", "Grille"),
              value: "grid",
            },
            {
              label: cmsNew("cms.new.blocks.carousel.label", "Carrousel"),
              value: "carousel",
            },
          ],
        },
        item1: {
          type: "object",
          label: cmsNew("cms.new.auto.label.temoignage_1", "Témoignage 1"),
          objectFields: {
            quote: {
              type: "textarea",
              label: cmsNew(
                "cms.new.auto.label.citation_traduisible",
                "Citation (traduisible)",
              ),
            },
            author: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.auteur_traduisible",
                "Auteur (traduisible)",
              ),
            },
            role: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.fonction_traduisible",
                "Fonction (traduisible)",
              ),
            },
            imageAttachmentId: {
              type: "custom",
              label: cmsNew("cms.new.auto.label.photo", "Photo"),
              render: ({ value, onChange }) => (
                <MediaPickerField
                  value={value as string | undefined}
                  onChange={onChange}
                  fieldName="imageAttachmentId"
                  label={cmsNew("cms.new.auto.label.photo", "Photo")}
                  allowedCatalogKinds={["image"]}
                />
              ),
            },
          },
        },
        item2: {
          type: "object",
          label: cmsNew("cms.new.auto.label.temoignage_2", "Témoignage 2"),
          objectFields: {
            quote: {
              type: "textarea",
              label: cmsNew(
                "cms.new.auto.label.citation_traduisible",
                "Citation (traduisible)",
              ),
            },
            author: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.auteur_traduisible",
                "Auteur (traduisible)",
              ),
            },
            role: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.fonction_traduisible",
                "Fonction (traduisible)",
              ),
            },
            imageAttachmentId: {
              type: "custom",
              label: cmsNew("cms.new.auto.label.photo", "Photo"),
              render: ({ value, onChange }) => (
                <MediaPickerField
                  value={value as string | undefined}
                  onChange={onChange}
                  fieldName="imageAttachmentId"
                  label={cmsNew("cms.new.auto.label.photo", "Photo")}
                  allowedCatalogKinds={["image"]}
                />
              ),
            },
          },
        },
        item3: {
          type: "object",
          label: cmsNew("cms.new.auto.label.temoignage_3", "Témoignage 3"),
          objectFields: {
            quote: {
              type: "textarea",
              label: cmsNew(
                "cms.new.auto.label.citation_traduisible",
                "Citation (traduisible)",
              ),
            },
            author: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.auteur_traduisible",
                "Auteur (traduisible)",
              ),
            },
            role: {
              type: "text",
              label: cmsNew(
                "cms.new.auto.label.fonction_traduisible",
                "Fonction (traduisible)",
              ),
            },
            imageAttachmentId: {
              type: "custom",
              label: cmsNew("cms.new.auto.label.photo", "Photo"),
              render: ({ value, onChange }) => (
                <MediaPickerField
                  value={value as string | undefined}
                  onChange={onChange}
                  fieldName="imageAttachmentId"
                  label={cmsNew("cms.new.auto.label.photo", "Photo")}
                  allowedCatalogKinds={["image"]}
                />
              ),
            },
          },
        },
        ...compositeInnerGapAlignFields({ withAlignItems: false }),
      },
      defaultProps: {
        layout: "grid",
        item1: { quote: "", author: "", role: "", imageAttachmentId: "" },
        item2: { quote: "", author: "", role: "", imageAttachmentId: "" },
        item3: { quote: "", author: "", role: "", imageAttachmentId: "" },
        gap: "lg",
      },
      render: ({ item1, item2, item3, layout, gap, gapCustomPx, ...rest }) => (
        <TestimonialsBlockPreview
          item1={item1}
          item2={item2}
          item3={item3}
          layout={layout}
          gap={gap}
          gapCustomPx={gapCustomPx}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    MapEmbed: {
      label: cmsNew("cms.new.auto.label.carte_embed", "Carte (embed)"),
      fields: {
        title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.titre_traduisible",
            "Titre (traduisible)",
          ),
        },
        embedUrl: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.url_dembed_iframe",
            "URL d'embed (iframe)",
          ),
        },
        aspect: {
          type: "select",
          label: cmsNew("cms.new.auto.label.proportions", "Proportions"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.16_9", "16:9"),
              value: "16/9",
            },
            { label: cmsNew("cms.new.auto.label.4_3", "4:3"), value: "4/3" },
            {
              label: cmsNew("cms.new.auto.label.carre", "Carré"),
              value: "1/1",
            },
            {
              label: cmsNew("cms.new.auto.label.portrait", "Portrait"),
              value: "tall",
            },
          ],
        },
        ...compositeInnerGapAlignFields({ withAlignItems: false }),
      },
      defaultProps: {
        title: cmsNew("cms.new.auto.title.d41d8cd9", ""),
        embedUrl: "",
        aspect: "16/9",
        gap: "md",
      },
      render: ({ embedUrl, title, aspect, gap, gapCustomPx }) => (
        <MapEmbedBlockPreview
          embedUrl={embedUrl}
          title={title}
          aspect={normalizeMapAspect(aspect)}
          gap={gap}
          gapCustomPx={gapCustomPx}
        />
      ),
    },

    VideoHero: {
      label: cmsNew("cms.new.blocks.videoHero.label", "Vidéo hero"),
      fields: {
        videoAttachmentId: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.video_media", "Vidéo / média"),
          render: ({ value, onChange }) => (
            <MediaPickerField
              value={value}
              onChange={onChange}
              fieldName="videoAttachmentId"
              label={cmsNew("cms.new.auto.label.video", "Vidéo")}
            />
          ),
        },
        posterAttachmentId: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.image_de_couverture_optionnel",
            "Image de couverture (optionnel)",
          ),
          render: ({ value, onChange }) => (
            <MediaPickerField
              value={value}
              onChange={onChange}
              fieldName="posterAttachmentId"
              label={cmsNew("cms.new.auto.label.poster", "Poster")}
            />
          ),
        },
        title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.titre_traduisible",
            "Titre (traduisible)",
          ),
          contentEditable: true,
        },
        subtitle: {
          type: "textarea",
          label: cmsNew(
            "cms.new.auto.label.sous_titre_traduisible",
            "Sous-titre (traduisible)",
          ),
        },
        ctaLabel: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_cta_traduisible",
            "Libellé CTA (traduisible)",
          ),
        },
        link: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.lien_du_cta", "Lien du CTA"),
          render: ({ value, onChange }) => (
            <LinkTargetField
              value={value as ButtonLink | undefined}
              onChange={onChange}
            />
          ),
        },
        overlay: {
          type: "select",
          label: cmsNew("cms.new.auto.label.overlay", "Overlay"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.aucun", "Aucun"),
              value: "none",
            },
            {
              label: cmsNew("cms.new.auto.label.clair", "Clair"),
              value: "light",
            },
            {
              label: cmsNew("cms.new.auto.label.sombre", "Sombre"),
              value: "dark",
            },
          ],
        },
        height: {
          type: "number",
          label: cmsNew(
            "cms.new.auto.label.hauteur_min_px",
            "Hauteur min. (px)",
          ),
          min: 240,
          max: 900,
        },
        ...compositeInnerGapAlignFields(),
      },
      defaultProps: {
        videoAttachmentId: "",
        posterAttachmentId: "",
        title: cmsNew("cms.new.auto.title.d41d8cd9", ""),
        subtitle: "",
        ctaLabel: "",
        link: { ...DEFAULT_BUTTON_LINK },
        overlay: "dark",
        height: 480,
        gap: "md",
        alignItems: "center",
      },
      render: ({
        videoAttachmentId,
        posterAttachmentId,
        title,
        subtitle,
        ctaLabel,
        link,
        overlay,
        height,
        gap,
        gapCustomPx,
        alignItems,
        ...rest
      }) => (
        <VideoHeroBlockPreview
          videoAttachmentId={videoAttachmentId}
          posterAttachmentId={posterAttachmentId}
          title={title}
          subtitle={subtitle}
          ctaLabel={ctaLabel}
          link={link}
          overlay={normalizeVideoHeroOverlay(overlay)}
          height={normalizeVideoHeroHeight(height)}
          gap={gap}
          gapCustomPx={gapCustomPx}
          alignItems={alignItems}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    PdfReader: {
      label: cmsNew("cms.new.blocks.pdfReader.label", "Lecteur PDF"),
      fields: {
        // Sélection STRUCTURELLE d'un PDF (table File). Stocke un `fileId`.
        fileId: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.document_pdf", "Document PDF"),
          render: ({ value, onChange }) => (
            <PdfPickerField
              value={value as string | null | undefined}
              onChange={onChange}
            />
          ),
        },
        title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.titre_optionnel_traduisible",
            "Titre (optionnel, traduisible)",
          ),
        },
        height: {
          type: "number",
          label: cmsNew(
            "cms.new.auto.label.hauteur_du_lecteur_px",
            "Hauteur du lecteur (px)",
          ),
          min: 200,
        },
      },
      defaultProps: {
        fileId: null,
        height: 720,
        title: cmsNew("cms.new.auto.title.d41d8cd9", ""),
      },
      render: ({ fileId, title, height }) => (
        <PdfReaderBlockPreview
          fileId={fileId}
          title={title}
          height={height}
        />
      ),
    },

    Html: {
      label: cmsNew("cms.new.auto.label.html_avance", "HTML (avancé)"),
      fields: {
        // HTML brut TRADUISIBLE (clé `<blockId>.html`) — assaini au save côté API
        // (extension de la sanitization `.body`). Bloc « avancé » : à réserver aux
        // utilisateurs avertis. Tout l'éditeur Puck est déjà gated `advancedCms`.
        html: {
          type: "textarea",
          label: cmsNew(
            "cms.new.auto.label.code_html_traduisible_assaini_au_save",
            "Code HTML (traduisible, assaini au save)",
          ),
        },
      },
      defaultProps: { html: "<p>Votre HTML ici…</p>" },
      render: ({ html }) => (
        <div
          className="cms-html-content mx-auto max-w-3xl"
          dangerouslySetInnerHTML={{ __html: html ?? "" }}
        />
      ),
    },

    News: {
      label: cmsNew("cms.new.blocks.news.label", "Actualités"),
      fields: {
        mode: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.source", "Source"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.automatique_recentes",
                "Automatique (récentes)",
              ),
              value: "auto",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.selection_manuelle",
                "Sélection manuelle",
              ),
              value: "manual",
            },
          ],
        },
        newsIds: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.actualites_choisies",
            "Actualités choisies",
          ),
          render: ({ value, onChange }) => (
            <NewsIdsField
              value={value as string[] | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: newsColumnCountField,
        limit: {
          type: "number",
          label: cmsNew(
            "cms.new.auto.label.nombre_maximum_0_toutes",
            "Nombre maximum (0 = toutes)",
          ),
          min: 0,
          max: 50,
        },
        featured: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.premiere_actualite_a_la_une",
            "Première actualité à la une",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
        ...compositeInnerGapAlignFields({ withAlignItems: false }),
      },
      defaultProps: {
        mode: "auto",
        newsIds: [],
        columns: 3,
        limit: 0,
        featured: true,
        gap: "lg",
      },
      render: ({ mode, newsIds, columns, limit, featured, gap, gapCustomPx }) => (
        <NewsPreview
          mode={mode}
          newsIds={newsIds}
          columns={columns}
          limit={limit}
          featured={featured}
          gap={gap}
          gapCustomPx={gapCustomPx}
        />
      ),
    },
  } satisfies Pick<RawComponentsConfig, "Heading" | "RichText" | "Button" | "Download" | "Quote" | "Icon" | "Divider" | "Media" | "Gallery" | "Carousel" | "SocialLinks" | "LogoGrid" | "Testimonials" | "MapEmbed" | "VideoHero" | "PdfReader" | "Html" | "News">;
}
